import React, { FC, useEffect, useRef, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import Markdown from 'markdown-to-jsx';

import { useCentralCalls } from '@src/rest/CentralCalls';
import { Flexbox, FlexItem, StyledContainer } from '@src/views/containers';
import { FlexAlign, FlexJustify, Size, CorvalUIColor } from '@src/types';
import { DashGridItem } from '../DashGrid';
//import { SparklesIcon } from '@src/_assets/icons/SparklesIcon';

type DashAiResponseProps = {
  /** Short title shown above the answer bubble (optional) */
  title?: string;
  /** Natural-language question/prompt to send to the AI */
  prompt: string;
  /** System context for the AI (e.g., “You are a dashboard analysis agent...”) */
  systemContext: string;
  /** Any structured data you want the AI to consider (will be JSON.stringified) */
  data?: unknown;
  /** Re-run when this changes; if omitted, runs once on mount */
  refreshKey?: string | number;
  item: DashGridItem;
};

const DashAiResponse: FC<DashAiResponseProps> = ({ title, item, prompt, systemContext, data, refreshKey }) => {
  const { GetAIResponse } = useCentralCalls();
  const [ fullText, setFullText ]   = useState<string>( '' ),
        [ typedText, setTypedText ] = useState<string>( '' ),
        [ isTyping, setIsTyping ]   = useState<boolean>( false ),
        [ error, setError ]         = useState<string | null>( null );

  const typingTimerRef = useRef<number | null>(null);

  // Fetch AI response when inputs change
  useEffect(() => {
    let isCancelled = false;
    setError(null);
    setFullText('');
    setTypedText('');
    setIsTyping(true);

    const run = async () => {
      try {
        console.log('running AII...');
        // Pass data as an attachment payload the way your GetAIResponse is shaped
        // (You used: GetAIResponse('', aiSearchValue, []))
        //console.log(JSON.stringify(data));
        //const attachments = data != null ? [JSON.stringify(data)] : [];
        console.log('running ai respond....');
        console.log(JSON.stringify(data));
        console.log(prompt);
        console.log(item);
        const res: string = await GetAIResponse(item.aiContext, 'Read the first prompt and respond with the relevant information from the data, Dont ask about follow ups. Just want your first initial thoughts based on the prompt: ' + (JSON.stringify(data) ?? ''), []);
        console.log(res);
        //const res = 'TEST skaldjfal;sj kf;aslkjfd ;lsakfjd;salkdjfsa;lfjk \n hey22';
        if (!isCancelled) setFullText(res.response ?? '');
      } catch (e: any) {
        if (!isCancelled) {
          setError(e?.message || 'Failed to get AI response.');
          setIsTyping(false);
        }
      }
    };

    run();

    return () => { isCancelled = true; };
  }, [prompt, systemContext, data, refreshKey]);

  // Fast typing effect
  useEffect(() => {
    if (!fullText) return;
    
    let i = 0;
    setTypedText('');
    setIsTyping(true);

    const step = () => {
      // type quickly: ~1–2ms per char for a snappy feel
      const CHUNK_SIZE = 3;
      const next = fullText?.slice(i, i + CHUNK_SIZE);
      i += CHUNK_SIZE;
      setTypedText(prev => prev + next);

      if (i < fullText.length) {
        typingTimerRef.current = window.setTimeout(step, 8);
      } else {
        setIsTyping(false);
      }
    };

    step();

    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };
  }, [fullText]);

  // Timestamp
  const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <StyledContainer className="ai-chat-box" bgColor={CorvalUIColor.WHITE}>
      {/* Optional title/header */}
      {/* {title && (
        <StyledContainer className="ai-chat-header-label">
          <Flexbox align={FlexAlign.CENTER} gap={Size.XS} justify={FlexJustify.START}>
            <Icon name="lightbulb" size="large" style={{ color: 'white' }} />
            <span className="ai-chat-header-text">{title}</span>
          </Flexbox>
        </StyledContainer>
      )} */}

      <StyledContainer
        className="ai-chat-messages"
        padding={[Size.MD, Size.MD, Size.MD, Size.MD]}
        style={{ overflowY: 'auto', minHeight: 140 }}
      >
        {/* AI bubble (styled to match your AIChatBox) */}
        <StyledContainer className="ai-chat-message ai">
          <Flexbox justify={FlexJustify.START} gap={Size.SM} align={FlexAlign.START}>
            <StyledContainer className="ai-chat-avatar" >
               <Icon name="bolt" size="large" style={{ color: 'gold' }} />
            </StyledContainer>

            <StyledContainer className="ai-chat-message-container">
              <StyledContainer className="ai-chat-bubble ai" padding={[Size.SM, Size.MD, Size.SM, Size.MD]}>
                <span className="ai-chat-text">
                  {error ? (
                    <Markdown>{`**Error:** ${error}`}</Markdown>
                  ) : (
                    <Markdown>{typedText || '...'}</Markdown>
                  )}
                </span>
              </StyledContainer>
              <StyledContainer className="ai-chat-timestamp ai">
                <span>
                   {isTyping && <span className="blinking-cursor"></span>}
                </span>
              </StyledContainer>
            </StyledContainer>
          </Flexbox>
        </StyledContainer>
      </StyledContainer>
    </StyledContainer>
  );
};

export default DashAiResponse;